import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import Loader from '../../components/Loader';
import EventInfiniteList from './EventInfiniteList';
import Title from '../../components/PageHeader/Title';
import Header from '../../components/PageHeader/Header';
import { EventList } from '../../store/modules/calendar/types';
import { MergeTemplate, Order, Form, FilterCondition, ConfiguredFields } from '../../store/modules/application/types';
import Filters from '../../components/Filters';
import moment from 'moment';
import { isEmpty } from 'lodash';

type EventProps = {
    eventList: EventList;
    isLoading: boolean;
    setError: Function;
    hasMore: boolean;
    isFiltersLoading: boolean;
    applyEventFilters: Function;
    getMergeTemplates: Function;
    mergeTemplatesList: MergeTemplate[];
    mergeInspectorPackDocuments: Function;
    templateLoading: boolean;
    isSearchFormLoading: boolean;
    searchForm: Form;
    getSearchForm: Function;
    filterSelections: Record<string, string | string[]>;
    setFilterSelections: Function;
    calendarColumnFields: ConfiguredFields;
};

const Calendar: FunctionComponent<EventProps> = (props) => {
    const {
        eventList,
        isLoading,
        setError,
        hasMore,
        isFiltersLoading,
        applyEventFilters,
        getMergeTemplates,
        mergeTemplatesList,
        mergeInspectorPackDocuments,
        templateLoading,
        isSearchFormLoading,
        searchForm,
        getSearchForm,
        filterSelections,
        setFilterSelections,
        calendarColumnFields,
    } = props;

    const [skip, setSkip] = useState<number>(0);
    const limit = 20;
    const [calendarSortOrder, setCalendarSortOrder] = useState<Order>('asc');
    const [calendarSortColumn, setCalendarSortColumn] = useState<string>('appointmentDateTime');
    const [listFilters, setListFilters] = useState<FilterCondition[]>([]);
    const defaultValue = { fromDate: moment().format('YYYY-MM-DD') };

    useEffect(() => {
        getSearchForm();
    }, [getSearchForm]);

    const refreshEventsList = useCallback(
        (listFilters, apiProps) => {
            // check if filter selections are applied before refreshing results
            if (
                Object.entries(filterSelections).length === 0 ||
                (Object.entries(filterSelections).length > 0 && listFilters.length > 0)
            )
                applyEventFilters(listFilters, {
                    limit,
                    orderBy: calendarSortOrder,
                    orderCol: calendarSortColumn,
                    ...apiProps,
                });
        },
        [applyEventFilters, limit, calendarSortOrder, calendarSortColumn, filterSelections],
    );

    useEffect(() => {
        refreshEventsList(listFilters, { skip });
        // The disable-next-line below is to avoid redundant callbacks due to updates in listFilters
        //eslint-disable-next-line
    }, [refreshEventsList, skip]);

    useEffect(() => {
        refreshEventsList(listFilters, { skip: 0 });
        setSkip(0);
    }, [refreshEventsList, listFilters]);

    useEffect(() => {
        const payload = {
            where: {
                type: 'Event',
            },
            fields: {
                buffer: false,
            },
        };
        getMergeTemplates(payload);
    }, [getMergeTemplates]);

    const updateEventSortBy = (sortBy: string) => {
        setCalendarSortColumn(sortBy);
        setSkip(0);
    };
    const updateEventSortOrder = (sortOrder: Order) => {
        setCalendarSortOrder(sortOrder);
        setSkip(0);
    };

    return (
        <div className="verticalFillContainer">
            <Header>
                <Grid container md={2} lg={2} direction="row" alignItems="center">
                    <Title titleText="Calendar" />
                </Grid>
                <Grid item md={10} lg={10}>
                    <Filters
                        searchForm={searchForm}
                        isLoading={isSearchFormLoading}
                        setListFilters={setListFilters}
                        handleFilterSelections={setFilterSelections}
                        selections={!isEmpty(filterSelections) ? filterSelections : defaultValue}
                    />
                </Grid>
            </Header>
            <div className="verticalFillContent">
                {!eventList || templateLoading ? (
                    <Loader />
                ) : (
                    <EventInfiniteList
                        eventList={eventList}
                        isLoading={isLoading}
                        skip={skip}
                        limit={limit}
                        setSkip={setSkip}
                        hasMore={hasMore}
                        setError={setError}
                        isFiltersLoading={isFiltersLoading}
                        listFilters={listFilters}
                        applyEventFilters={applyEventFilters}
                        mergeTemplatesList={mergeTemplatesList}
                        mergeInspectorPackDocuments={mergeInspectorPackDocuments}
                        sortBy={calendarSortColumn}
                        sortOrder={calendarSortOrder}
                        setSortOrder={updateEventSortOrder}
                        setSortColumn={updateEventSortBy}
                        calendarColumnFields={calendarColumnFields}
                    />
                )}
            </div>
        </div>
    );
};

export default Calendar;
